// @flow

// Libs
import React, { Component } from 'react'

// Utils
import traverse from 'fc-utils/traverse'

// Components
import { Errors } from 'hfty-components-errors'
import Textfield from '@react-mdc/textfield'
import FormField from '@react-mdc/form-field'
import Button from '@react-mdc/button'

// Translations
import messages from '../messages'
import { FormattedMessage } from 'react-intl'

// Constants
import {
  PROP_NAME,
  PROP_EMAIL,
  PROP_SUBJECT,
  PROP_MESSAGE,
} from 'hfty-model-contact-us'
import { MOBILE_BREAK_POINT } from 'hfty'

// Styles
import { grey, lightGrey } from 'hfty-styles/src/colors'
const styles = {
  container: {
    flex: '1 1 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 5,
    padding: '0 10px',
    flex: '1 1 auto',
    minHeight: '6vh',
    minWidth: '23vh',
    color: grey(),
  },
  textFieldHeaders: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    width: '70vw',
  },
  textFieldError: {
    color: '#F4537F',
  },
  form: {
    margin: 5,
    display: 'flex',
    flexDirection: 'column',
    color: '#ffffff',
    backgroundColor: lightGrey(),
    padding: 20,
    boxShadow: '0 0 3px rgba(0,0,0,0.3)',
    justifyContent: 'space-around',
    alignItems: 'center',
    width: '70vw',
  },
  messageField: {
    backgroundColor: '#fff',
    width: '20vw',
    padding: '4px 3px',
  },
  successMessage: {
    display: 'flex',
    flexWrap: 'wrap',
    color: '#008810',
  },
  span: {
    display: 'inline-flex',
    marginTop: 10,
  },
  link: {
    display: 'flex',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  divSpace: (errors: boolean): Object => ({
    marginLeft: 5,
    color: errors ? '#F4537F' : '#979797',
    fontSize: '12px',
  }),
  mobileFormField: {
    height: 50,
  },
}

// Local Types
type Props = {
  operation: ?Object,
  viewport: {
    width: number,
  },
  actions: {
    onSubmit: Function,
    onChange: Function,
  }
}

// Getter
const getProp = (props: Object, propName: string): string =>
  traverse(['operation', 'model', propName], { default: '' })(props)

// Exports
export class ContactUs extends Component {
  props: Props

  constructor (props: Props) {
    super(props)

    this.state = {
      fieldError: null,
      userName: null,
    }

    this.onFieldChange = this.onFieldChange.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
  }

  componentWillUnmount () {
    this.props.actions.onChange({
      model: {},
      submitting: false,
      success: false,
    })
  }

  onFieldChange (e: Event, prop: string) {
    this.props.actions.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        [prop]: traverse(['currentTarget', 'value'], { default: '' })(e),
      },
      submitting: false,
      success: false,
    })
  }

  onSubmit (e: Event & { currentTarget: HTMLInputElement }) {
    e.preventDefault()
    let { model } = this.props.operation

    if (!model || (!model.name || !model.name.length || !model.email || !model.email.length ||
      !model.message || !model.message.length || !model.subject || !model.subject.length)) {
      this.setState({
        fieldError: 'youMustFillInAllRequiredFields',
      })
    } else {
      this.setState({
        fieldError: null,
        userName: getProp(this.props, PROP_NAME),
      })
      this.props.actions.onSubmit()
    }
  }

  renderForm (): ?React.Element<any> {
    let mobile = this.props.viewport.width < MOBILE_BREAK_POINT

    return (
      <form
        style={mobile ? {...styles.form, width: '80%'} : styles.form}
        onSubmit={this.onSubmit}
      >
        <div style={styles.title}>
          <p><FormattedMessage {...messages['contactUs']} /></p>
        </div>
        {!this.props.operation.success
          ? <Errors errors={this.props.operation.errors} messages={messages} />
          : this.renderSuccessMessage()}
        <div style={styles.textFieldError}>
          {this.state.fieldError
            ? <FormattedMessage {...messages['youMustFillInAllRequiredFields']} />
            : null }
        </div>
        <div style={mobile ? {...styles.textFieldHeaders, flexDirection: 'column'} : styles.textFieldHeaders}>
          <FormField style={mobile ? styles.mobileFormField : {}}>
            <Textfield>
              <Textfield.Label htmlFor='contact-us-name'>
                <FormattedMessage {...messages['name']} />
              </Textfield.Label>
              <Textfield.Input
                id='contact-us-name'
                required
                style={mobile ? {...styles.messageField, width: '60vw'} : styles.messageField}
                value={getProp(this.props, PROP_NAME)}
                onChange={(e: Event) => {
                  this.onFieldChange(e, PROP_NAME)
                }}
              />
            </Textfield>
          </FormField>
          <FormField style={mobile ? styles.mobileFormField : {}}>
            <Textfield required>
              <Textfield.Label htmlFor='contact-us-email'>
                <FormattedMessage {...messages['email']} />
              </Textfield.Label>
              <Textfield.Input
                id='contact-us-email'
                type='email'
                pattern='^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5})$'
                data-pattern-error='Please input a valid email address!'
                required
                style={mobile ? {...styles.messageField, width: '60vw'} : styles.messageField}
                value={getProp(this.props, PROP_EMAIL)}
                onChange={(e: Event) => {
                  this.onFieldChange(e, PROP_EMAIL)
                }}
              />
            </Textfield>
          </FormField>
          <FormField style={mobile ? styles.mobileFormField : {}}>
            <Textfield>
              <Textfield.Label htmlFor='contact-us-subject'>
                <FormattedMessage {...messages['subject']} />
              </Textfield.Label>
              <Textfield.Input
                id='contact-us-subject'
                required
                style={mobile ? {...styles.messageField, width: '60vw'} : styles.messageField}
                value={getProp(this.props, PROP_SUBJECT)}
                onChange={(e: Event) => {
                  this.onFieldChange(e, PROP_SUBJECT)
                }}
              />
            </Textfield>
          </FormField>
        </div>
        <div style={styles.textFieldHeaders}>
          <FormField>
            <Textfield multiline required>
              <Textfield.Input.Meta>
                <textarea
                  id='contact-us-message'
                  required
                  value={getProp(this.props, PROP_MESSAGE)}
                  onChange={(e: Event) => {
                    this.onFieldChange(e, PROP_MESSAGE)
                  }}
                  style={mobile ? {...styles.messageField, width: '45vh'} : {...styles.messageField, width: '70vw'}}
                  rows={8}
                  cols={40} />
              </Textfield.Input.Meta>
              <Textfield.Label htmlFor='contact-us-message'>
                <FormattedMessage {...messages['message']} />
              </Textfield.Label>
            </Textfield>
          </FormField>
        </div>
        <FormField>
          <Button
            raised
            primary
            type='submit'
          >
            <FormattedMessage {...messages['submitForm']} />
          </Button>
        </FormField>
      </form>
    )
  }

  renderSuccessMessage (): ?React.Element<any> {
    return (
      <p style={styles.successMessage}>
        <FormattedMessage {...messages['successMessage']} values={{name: this.state.userName}} />
      </p>
    )
  }

  render (): ?React.Element<any> {
    return (
      <div style={styles.container}>
        {this.renderForm()}
      </div>
    )
  }
}

export default ContactUs
